/**
 * FlowManagementClient — thin wrapper over the generated Octopus Flow
 * Management TypeScript client (@amzn/octopus-flow-management-service-client,
 * `OctopusFlowManagementClient`). Used by the Event Hub module to mint
 * QuickSight embed URLs so we can render Octopus-owned dashboards inside Rocky
 * (mirrors AWSOctopusPortal's `maApi` + `maClient`).
 *
 * Auth chain (same as EventHubClient — see eventHubCredentials):
 *   Federate id_token → EventHub Cognito Identity Pool (aws-cn / BJS)
 *   → temp AWS creds → SigV4 (execute-api, ZHY) → Octopus Flow Mgmt API.
 *
 * SIGNING NAME OVERRIDE: the service model declares `@sigv4(name:
 * "awsoctopusflowmanagementservice")`, but the API sits behind API Gateway,
 * which validates SigV4 signed for `execute-api`. So we override the client's
 * default signer with a SignatureV4 that signs for `execute-api` (matching how
 * AWSOctopusPortal/AgentHubPortal wire their client via resolveSigV4AuthConfig).
 */

import {
  OctopusFlowManagementClient,
  GenerateEmbedUrlForAnonymousUserCommand,
  GenerateEmbedUrlForRegisteredUserCommand,
  type GenerateEmbedUrlForAnonymousUserInput,
  type GenerateEmbedUrlForRegisteredUserInput,
} from '@amzn/octopus-flow-management-service-client';
import { SignatureV4 } from '@smithy/signature-v4';
import { Sha256 } from '@aws-crypto/sha256-js';
import { appConfig } from '../../config';
import { API_REGION, getEventHubCredentials } from './eventHubCredentials';

let _client: OctopusFlowManagementClient | undefined;

function getClient(): OctopusFlowManagementClient {
  if (_client) return _client;

  const { flowMgmtApiEndpoint } = appConfig.eventHub;
  if (!flowMgmtApiEndpoint || flowMgmtApiEndpoint === 'TBD') {
    throw new Error(
      `[FlowManagementClient] No Octopus Flow Management endpoint configured for stage "${appConfig.stage}"`,
    );
  }

  const credentials = getEventHubCredentials();

  // Override the model's sigv4 name with `execute-api` (API Gateway requirement).
  const signer = new SignatureV4({
    service: 'execute-api',
    region: API_REGION,
    credentials,
    sha256: Sha256,
  });

  _client = new OctopusFlowManagementClient({
    region: API_REGION,
    endpoint: flowMgmtApiEndpoint,
    credentials,
    // Provider form matches the client's `signer?: (authScheme?) => Promise<RequestSigner>`.
    signer: () => Promise.resolve(signer),
  });
  return _client;
}

// Browser origin passed through so the service can set QuickSight AllowedDomains
// to this exact URL (Talos V2423031023 / V2422897450). Field exists on the service
// model after the AWSOctopusFlowManagementService + Model CR lands; the unknown
// cast lets this build against the current generated client.
const allowedDomain = typeof window !== 'undefined' ? window.location.origin : undefined;

/**
 * Mint an anonymous QuickSight embed URL for the given dashboards.
 * `embdedUrl` (sic) is the field name in the service model.
 */
export async function generateEmbedUrlForAnonymousUser(
  dashboardIds: string[],
  accountShortName?: string,
) {
  const request = { dashboardIds, accountShortName, allowedDomain } as unknown as GenerateEmbedUrlForAnonymousUserInput;
  return getClient().send(new GenerateEmbedUrlForAnonymousUserCommand(request));
}

/**
 * Mint a registered-user QuickSight embed URL (per-user row-level security).
 * `userAlias` is the operator's login (e.g. useAuth().uid).
 */
export async function generateEmbedUrlForRegisteredUser(
  dashboardIds: string[],
  userAlias: string,
  accountShortName?: string,
) {
  const request = {
    dashboardIds,
    userAlias,
    accountShortName,
    allowedDomain,
  } as unknown as GenerateEmbedUrlForRegisteredUserInput;
  return getClient().send(new GenerateEmbedUrlForRegisteredUserCommand(request));
}
